import { useState } from 'react';
import { Modal } from '../components/Modal';
import { createBusiness, PlatformApiError, type PlanOption } from './api';
import { slugify } from './format';

// Fase 25B: alta de negocio piloto (negocio + Owner inicial + plan). El password del Owner NO se
// persiste en el cliente despues del submit (se limpia); no se muestra luego en el detalle. El plan por
// defecto es PILOT. No entra automaticamente como el tenant.

export function CreateBusinessModal({ plans, onClose, onCreated }: { plans: PlanOption[]; onClose: () => void; onCreated: (businessId: string) => void }) {
  const [businessName, setBusinessName] = useState('');
  const [businessSlug, setBusinessSlug] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);
  const [planCode, setPlanCode] = useState(plans.find((p) => p.code === 'PILOT') ? 'PILOT' : (plans[0]?.code ?? ''));
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();

  const effectiveSlug = slugTouched ? businessSlug : slugify(businessName);
  const canSubmit = businessName.trim().length >= 2 && effectiveSlug.length >= 2 && planCode && firstName.trim() && lastName.trim() && /.+@.+\..+/.test(email) && username.trim().length >= 3 && /^\d{4,8}$/.test(password);

  const submit = async () => {
    if (busy || !canSubmit) return;
    setBusy(true); setError(undefined);
    try {
      const detail = await createBusiness({
        businessName: businessName.trim(), businessSlug: effectiveSlug, planCode,
        owner: { firstName: firstName.trim(), lastName: lastName.trim(), email: email.trim(), username: username.trim(), password },
      });
      setPassword(''); // no conservar el password en el cliente
      onCreated(detail.id);
    } catch (err) {
      setPassword('');
      setError(err instanceof PlatformApiError ? err.message : 'No fue posible crear el negocio.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      title="Crear negocio"
      size="wide"
      isBusy={busy}
      onClose={onClose}
      footer={<>
        <button className="button button--ghost" disabled={busy} onClick={onClose} type="button">Cancelar</button>
        <button className="button button--primary" disabled={busy || !canSubmit} onClick={() => void submit()} type="button">Crear negocio</button>
      </>}
    >
      {error && <div className="alert alert--error" role="alert">{error}</div>}
      <h4 className="platform-form__section">Datos del negocio</h4>
      <div className="platform-form__grid">
        <label className="field"><span>Nombre *</span><input value={businessName} maxLength={150} onChange={(e) => setBusinessName(e.target.value)} autoFocus /></label>
        <label className="field"><span>Identificador (slug) *</span><input value={effectiveSlug} maxLength={60} onChange={(e) => { setSlugTouched(true); setBusinessSlug(slugify(e.target.value)); }} /></label>
        <label className="field"><span>Plan *</span>
          <select value={planCode} onChange={(e) => setPlanCode(e.target.value)}>
            {plans.map((p) => <option key={p.code} value={p.code}>{p.name}</option>)}
          </select>
        </label>
      </div>

      <h4 className="platform-form__section">Owner inicial</h4>
      <div className="platform-form__grid">
        <label className="field"><span>Nombre *</span><input value={firstName} maxLength={80} onChange={(e) => setFirstName(e.target.value)} /></label>
        <label className="field"><span>Apellido *</span><input value={lastName} maxLength={80} onChange={(e) => setLastName(e.target.value)} /></label>
        <label className="field"><span>Correo *</span><input type="email" value={email} maxLength={200} onChange={(e) => setEmail(e.target.value)} /></label>
        <label className="field"><span>Usuario *</span><input value={username} maxLength={60} onChange={(e) => setUsername(e.target.value)} /></label>
        <label className="field"><span>Contraseña inicial (4–8 dígitos) *</span><input type="password" value={password} inputMode="numeric" onChange={(e) => setPassword(e.target.value)} /></label>
      </div>
      <p className="platform-form__hint">La contraseña se comparte de forma segura con el Owner. No se mostrará después de crear el negocio.</p>
    </Modal>
  );
}
